import {
	computeExportDimensions,
	type ImageRect,
} from "./image-display-bounds";

const RECT_FILL_ALPHA = 0.12;
const RECT_FILL_ALPHA_SELECTED = 0.2;
const RECT_BORDER_WIDTH = 1.5;
const RECT_BORDER_WIDTH_SELECTED = 2;
const BADGE_OFFSET = 6;
const BADGE_SIZE_MIN = 22;
const BADGE_SIZE_MAX = 28;
const BADGE_SIZE_RATIO = 0.32;
const BADGE_INNER_SHRINK = 8;
const BADGE_INNER_RATIO = 0.72;
const BADGE_FONT_RATIO = 0.4;
const BADGE_FONT_MIN = 10;
const BADGE_THICK_BORDER_AT = 28;
const HEX_COLOR_LENGTH = 6;

export type DamageRectDraw = ImageRect & {
	letter: string;
	color: string;
	selected?: boolean;
};

type DamageBadgeDraw = {
	ctx: CanvasRenderingContext2D;
	left: number;
	top: number;
	letter: string;
	color: string;
	size: number;
};

function hexToRgba(hex: string, alpha: number): string {
	const normalized = hex.replace("#", "");
	if (normalized.length !== HEX_COLOR_LENGTH) {
		return `rgba(0,128,254,${alpha})`;
	}

	const red = Number.parseInt(normalized.slice(0, 2), 16);
	const green = Number.parseInt(normalized.slice(2, 4), 16);
	const blue = Number.parseInt(normalized.slice(4, 6), 16);
	return `rgba(${red},${green},${blue},${alpha})`;
}

function badgeSizeForRect(width: number, height: number): number {
	const minSide = Math.min(width, height);
	return Math.round(
		Math.min(
			BADGE_SIZE_MAX,
			Math.max(BADGE_SIZE_MIN, minSide * BADGE_SIZE_RATIO)
		)
	);
}

function drawDamageBadge({
	ctx,
	left,
	top,
	letter,
	color,
	size,
}: DamageBadgeDraw): void {
	const centerX = left + size / 2;
	const centerY = top + size / 2;
	const inner = Math.max(size - BADGE_INNER_SHRINK, size * BADGE_INNER_RATIO);
	const fontSize = Math.max(
		BADGE_FONT_MIN,
		Math.round(size * BADGE_FONT_RATIO)
	);
	const borderWidth = size >= BADGE_THICK_BORDER_AT ? 2 : 1.5;

	ctx.beginPath();
	ctx.arc(centerX, centerY, size / 2, 0, Math.PI * 2);
	ctx.strokeStyle = color;
	ctx.lineWidth = borderWidth;
	ctx.stroke();

	ctx.beginPath();
	ctx.arc(centerX, centerY, inner / 2, 0, Math.PI * 2);
	ctx.fillStyle = "#FFFFFF";
	ctx.fill();

	ctx.fillStyle = color;
	ctx.font = `600 ${fontSize}px ui-sans-serif, system-ui, sans-serif`;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	ctx.fillText(letter, centerX, centerY);
}

export function drawDamageRect(
	ctx: CanvasRenderingContext2D,
	rect: DamageRectDraw
): void {
	const isSelected = Boolean(rect.selected);
	ctx.fillStyle = hexToRgba(
		rect.color,
		isSelected ? RECT_FILL_ALPHA_SELECTED : RECT_FILL_ALPHA
	);
	ctx.fillRect(rect.x, rect.y, rect.width, rect.height);

	ctx.strokeStyle = rect.color;
	ctx.lineWidth = isSelected ? RECT_BORDER_WIDTH_SELECTED : RECT_BORDER_WIDTH;
	ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);

	const badgeSize = badgeSizeForRect(rect.width, rect.height);
	drawDamageBadge({
		ctx,
		left: rect.x + BADGE_OFFSET,
		top: rect.y + BADGE_OFFSET,
		letter: rect.letter,
		color: rect.color,
		size: badgeSize,
	});
}

export function bakeAnnotatedJpeg(
	image: HTMLImageElement,
	markers: Array<ImageRect & { letter: string; color: string }>,
	quality: number,
	maxLongEdge: number
): Promise<Blob> {
	const exportSize = computeExportDimensions(
		{ width: image.width, height: image.height },
		maxLongEdge
	);
	const canvas = document.createElement("canvas");
	canvas.width = exportSize.width;
	canvas.height = exportSize.height;
	const ctx = canvas.getContext("2d");

	if (!ctx) {
		return Promise.reject(
			new Error("Não foi possível rasterizar as marcações.")
		);
	}

	ctx.drawImage(image, 0, 0, exportSize.width, exportSize.height);
	const scale = exportSize.width / image.width;

	for (const marker of markers) {
		drawDamageRect(ctx, {
			x: marker.x * scale,
			y: marker.y * scale,
			width: marker.width * scale,
			height: marker.height * scale,
			letter: marker.letter,
			color: marker.color,
		});
	}

	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (!blob) {
					reject(new Error("Não foi possível gerar a foto anotada."));
					return;
				}
				resolve(blob);
			},
			"image/jpeg",
			quality
		);
	});
}
